import {
  ExperimentOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { type ItemType, type MenuDividerType } from 'antd5/es/menu/interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { Button, Dropdown, Header, Spin, useMessage, WarningAlert } from 'components/ui';
import { UserAvatar } from 'components/UserAvatar';
import { useGenericProgressContext } from 'horizon/components/GlobalProgressIndicator/GenericProgress/GenericProgressContext';
import { GlobalProgressButton } from 'horizon/components/GlobalProgressIndicator/GlobalProgressButton';
import { CopyUrlButton } from 'horizon/components/OrgRedirect';
import { useAccountContext } from 'utils/account/AccountContext';
import { useAuthContext } from 'utils/auth';
import { getPreferredThemeToApply } from 'utils/color/horizonTheme';
import { cacheLanguage, getPreferredLanguageToApply } from 'utils/i18n';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';
import { MULTI_LANGUAGE, useDisableReleaseToggles } from 'utils/release-toggles';
import { notEmpty } from 'utils/types';

import { CustomerSwitcher } from './CustomerSwitcher';
import { useUploadContext } from './FileUploader';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NewReleasesButton } from './NewReleases';
import { NotificationsButton } from './Notifications';

const StyledHeader = styled(Header)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: auto;
  min-height: 64px;
  padding: 1rem !important;
  background: ${({ theme }) => theme.secondaryBackgroundColor} !important;

  h1 {
    color: ${({ theme }) => theme.textColor} !important;
  }

  > div {
    &:nth-of-type(1) {
      line-height: 1.5;
    }
    &:nth-of-type(2) {
      height: calc(64px - 2rem);
    }
  }
`;

const horizonUserMenuItems = (
  handleLogout: () => void,
  canManageReleaseToggles: boolean,
  releaseTogglesDisabled: boolean,
  toggleDisabledReleaseToggles: () => void,
  t: (key: string) => string
): ItemType[] => {
  const menuItems: ItemType[] = [
    {
      key: 'user-guide',
      label: (
        <a
          href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('menu.userGuide', 'User Guide')}
        </a>
      ),
      icon: <InfoCircleOutlined />,
    },
    {
      key: 'support',
      label: (
        <a href="https://support.skyspecs.com/" target="_blank" rel="noopener noreferrer">
          {t('menu.support', 'Support')}
        </a>
      ),
      icon: <QuestionCircleOutlined />,
    },
    { key: 'divider-1', type: 'divider' } as MenuDividerType,
    {
      key: 'settings',
      label: <Link to="/settings">{t('navigation.settings', 'Settings')}</Link>,
      icon: <SettingOutlined />,
    },
    canManageReleaseToggles && {
      key: 'new-features',
      label: (
        <Link onClick={toggleDisabledReleaseToggles} to="#" data-testid="user-menu-release-toggles">
          {releaseTogglesDisabled
            ? t('menu.enableFeatures', 'Enable New Features')
            : t('menu.disableFeatures', 'Disable New Features')}
        </Link>
      ),
      icon: <ExperimentOutlined />,
    },
    canManageReleaseToggles && ({ key: 'divider-2', type: 'divider' } as MenuDividerType),
    {
      key: 'logout',
      label: t('menu.logout', 'Logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ].filter(notEmpty);

  return menuItems;
};

export function GlobalHeader({ content }: { content: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const message = useMessage();
  const { canManageReleaseToggles, releaseTogglesDisabled, toggleDisabledReleaseToggles } =
    useDisableReleaseToggles();
  const [onLine, setOnLine] = useState(navigator.onLine);
  const { actionInProgress } = useGenericProgressContext();
  const { uploading, modalVisible, setConfirmProps } = useUploadContext();
  const { logout, user } = useAuthContext();
  const { hasReleaseToggle, loading: accountLoading, user: accountUser } = useAccountContext();
  const hasMultiLanguage = hasReleaseToggle(MULTI_LANGUAGE);

  // i18n initializes before UserDetails, so localStorage is only a bootstrap cache.
  // Once the account is loaded, the server language (or English when unset) overwrites
  // that cache so a previous user on this browser cannot leak a language.
  useEffect(() => {
    if (accountLoading || !accountUser?.id) {
      return;
    }

    const language = getPreferredLanguageToApply({
      currentLanguage: i18n.language,
      hasMultiLanguage,
      preferredLanguage: accountUser?.preferences?.language,
    });

    if (language) {
      cacheLanguage(language);
      i18n.changeLanguage(language);
      window.location.reload();
    }
  }, [accountLoading, accountUser, hasMultiLanguage, i18n]);

  // ThemeProvider reads ui-theme before UserDetails. Once the account is loaded,
  // apply the server theme, or Light when it is unset, so the cache cannot leak.
  useEffect(() => {
    if (accountLoading || !accountUser?.id) {
      return;
    }

    const nextTheme = getPreferredThemeToApply({
      currentTheme: theme.themeName,
      preferredTheme: accountUser?.preferences?.theme,
    });

    if (nextTheme) {
      theme.onChange(nextTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ThemeProvider recreates onChange each render
  }, [accountLoading, accountUser?.id, accountUser?.preferences?.theme, theme.themeName]);

  const handleLogout = () => {
    if (uploading) {
      setConfirmProps({
        onOk: () => {
          // FIXME: Remove debug after logout problem is solved
          console.debug('GlobalHeader: logging out via Uploads in progress confirmation');
          logout({ broadcast: true });
        },
        message: t(
          'menu.uploadInProgress',
          'You have uploads in progress. Are you sure you want to log out?'
        ),
      });
    } else {
      // FIXME: Remove debug after logout problem is solved
      console.debug('GlobalHeader: logging out directly');
      logout({ broadcast: true });
    }
  };

  const displayConnectionStatus = useCallback(() => {
    if (navigator.onLine) {
      message.success(t('notifications.success.onlineRestored', 'You are back online'), 5);
    }

    setOnLine(navigator.onLine);
  }, [message, t]);

  const openUserGuide = useCallback((e: KeyboardEvent) => {
    // F1 for hot key
    if (e.code === 'F1') {
      // prevent chrome from opening help menu
      e.preventDefault();
      const win = window.open('https://guide.skyspecs.com');
      if (win) {
        win.opener = null;
      }
    }
  }, []);

  useEffect(() => {
    // notify online/offline
    window.addEventListener('offline', displayConnectionStatus);
    window.addEventListener('online', displayConnectionStatus);
    // for hotkeys
    window.addEventListener('keydown', openUserGuide);

    return () => {
      window.removeEventListener('offline', displayConnectionStatus);
      window.removeEventListener('online', displayConnectionStatus);
      window.removeEventListener('keydown', openUserGuide);
    };
  }, [displayConnectionStatus, openUserGuide]);

  return (
    <StyledHeader id="global-header-component" data-testid="global-header">
      <FlexContainer>{content}</FlexContainer>
      <FlexContainer>
        {!onLine && (
          <SpacedContainer>
            <WarningAlert
              showIcon={true}
              message={t('menu.connectionOffline', 'You are offline. Attempting to reconnect...')}
            />
          </SpacedContainer>
        )}

        {actionInProgress || (uploading && !modalVisible) ? (
          <SpacedContainer>
            <GlobalProgressButton />
          </SpacedContainer>
        ) : null}

        <SpacedContainer>
          <CopyUrlButton />
        </SpacedContainer>

        <NotificationsButton />

        <SpacedContainer>
          <NewReleasesButton />
        </SpacedContainer>

        {hasMultiLanguage && (
          <SpacedContainer>
            <LanguageSwitcher showTooltip={false} />
          </SpacedContainer>
        )}

        <CustomerSwitcher />

        <Dropdown
          className="user-menu-dropdown"
          menu={{
            items: horizonUserMenuItems(
              handleLogout,
              canManageReleaseToggles,
              releaseTogglesDisabled,
              toggleDisabledReleaseToggles,
              t
            ),
          }}
          trigger={['hover']}
        >
          <Button
            type="text"
            style={{ border: 'none', padding: 0, height: 'auto' }}
            data-testid="user-menu-icon"
            aria-label={t('menu.userMenu', 'User menu')}
          >
            {user ? <UserAvatar user={user} /> : <Spin size="small" />}
          </Button>
        </Dropdown>
      </FlexContainer>
    </StyledHeader>
  );
}
